import { Slot } from '../Modules/Slot/Model/Slot';
import { User } from '../Modules/User/Model/User';
import { isSlotOperationRestricted, getRestrictionMessage } from './timeRestriction';
import { isAdpa99Username } from './usernameSuffix';

function toLocalDateOnly(value?: string | null): Date | null {
  if (!value) return null;

  const datePart = value.slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
  if (match) {
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(0, 0, 0, 0);
  return parsed;
}

function getTodayDateOnly(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

function isSameDateOnly(a: Date | null, b: Date): boolean {
  return !!a && a.getTime() === b.getTime();
}

export interface SlotPermissionResult {
  canExtend: boolean;
  canEdit: boolean;
  canBatchEdit: boolean;
  canRefund: boolean;
  canDelete: boolean;
  reason?: string;
}

/**
 * 슬롯 상태에 따른 권한을 확인합니다.
 */
export function getSlotPermissions(
  slot: Slot,
  currentUser: User | null,
  isRestricted: boolean = false
): SlotPermissionResult {
  if (!currentUser) {
    return {
      canExtend: false,
      canEdit: false,
      canBatchEdit: false,
      canRefund: false,
      canDelete: false,
      reason: '로그인이 필요합니다.'
    };
  }

  const isAdmin = currentUser.grade === 'ADMIN';
  const isSeller = currentUser.grade === 'SELLER';
  // adpa99 쪽만 이미 시작된 슬롯도 (환불처럼) 삭제 가능
  const canDeleteStarted = isAdpa99Username(currentUser.username) && !isSeller && !isSlotOperationRestricted(currentUser) && !isRestricted;
  const todayDateOnly = getTodayDateOnly();
  const endDateOnly = toLocalDateOnly(slot.end_date);
  const startDateOnly = toLocalDateOnly(slot.start_date);
  const isExpiredToday = isSameDateOnly(endDateOnly, todayDateOnly);
  const isNotStarted = !!startDateOnly && startDateOnly > todayDateOnly; // 시작일이 오늘 이후인 슬롯
  const isActive =
    !!startDateOnly &&
    !!endDateOnly &&
    startDateOnly <= todayDateOnly &&
    endDateOnly > todayDateOnly; // 작동 중인 슬롯 (시작일 <= 오늘 < 종료일)
  const isStartAfterToday = isNotStarted; // 시작일이 내일 이상인 슬롯 (오늘/과거 제외)

  // 시간 제한 확인
  const timeRestricted = isSlotOperationRestricted(currentUser);
  // 셀러는 연장/환불 UseCase에서 차단됨 → 버튼도 비활성
  const canSellerMutateOps = !isSeller;

  switch (slot.status) {
    case '정상':
      // 미개시 정상 슬롯은 조건에 따라 삭제 가능
      if (isNotStarted) {
        return {
          canExtend: canSellerMutateOps && !timeRestricted && !isRestricted,
          canEdit: !timeRestricted && !isRestricted,
          canBatchEdit: !timeRestricted && !isRestricted,
          canRefund: false, // 미개시 슬롯은 환불 불가
          // 삭제: ADMIN은 언제나, 대행사/총판은 제한 시간 외 + 외부 제한 없음, 셀러는 불가
          canDelete: isStartAfterToday && (isAdmin || (!isSeller && !timeRestricted && !isRestricted)),
          reason: isSeller
            ? '셀러는 연장 권한이 없습니다.'
            : timeRestricted
              ? getRestrictionMessage('수정', currentUser)
              : undefined
        };
      }
      // 시작된 정상 슬롯은 원칙적으로 삭제 불가. 단, adpa99는 환불처럼 삭제 가능
      return {
        canExtend: canSellerMutateOps && !timeRestricted && !isRestricted,
        canEdit: !timeRestricted && !isRestricted,
        canBatchEdit: !timeRestricted && !isRestricted,
        canRefund: canSellerMutateOps && !timeRestricted && !isRestricted,
        canDelete: canDeleteStarted,
        reason: isSeller
          ? '셀러는 연장/환불 권한이 없습니다.'
          : timeRestricted
            ? getRestrictionMessage('수정', currentUser)
            : isActive && !canDeleteStarted
              ? '작동 중인 슬롯은 삭제할 수 없습니다.'
              : undefined
      };

    case '연장':
      if (isStartAfterToday) {
        return {
          canExtend: canSellerMutateOps && !timeRestricted && !isRestricted,
          canEdit: !timeRestricted && !isRestricted,
          canBatchEdit: !timeRestricted && !isRestricted,
          canRefund: canSellerMutateOps && !timeRestricted && !isRestricted,
          // 삭제: ADMIN은 언제나, 대행사/총판은 제한 시간 외 + 외부 제한 없음, 셀러는 불가
          canDelete: isAdmin || (!isSeller && !timeRestricted && !isRestricted),
          reason: isSeller
            ? '셀러는 연장/환불 권한이 없습니다.'
            : timeRestricted
              ? getRestrictionMessage('수정', currentUser)
              : undefined
        };
      }
      return {
        canExtend: canSellerMutateOps && !timeRestricted && !isRestricted,
        canEdit: !timeRestricted && !isRestricted,
        canBatchEdit: !timeRestricted && !isRestricted,
        canRefund: canSellerMutateOps && !timeRestricted && !isRestricted,
        // 정책: 미개시 슬롯만 삭제 가능. 단, adpa99는 시작된 슬롯도 삭제 가능
        canDelete: canDeleteStarted,
        reason: isSeller
          ? '셀러는 연장/환불 권한이 없습니다.'
          : timeRestricted
            ? getRestrictionMessage('수정', currentUser)
            : isActive && !canDeleteStarted
              ? '작동 중인 슬롯은 삭제할 수 없습니다.'
              : undefined
      };

    case '환불':
      return {
        canExtend:
          canSellerMutateOps && isExpiredToday && !timeRestricted && !isRestricted,
        canEdit: false,
        canBatchEdit: false,
        canRefund: false,
        // 정책: 미개시 정상 슬롯만 삭제 가능 → 환불 상태 삭제 불가
        canDelete: false,
        reason: isSeller
          ? '셀러는 연장 권한이 없습니다.'
          : isExpiredToday && (timeRestricted || isRestricted)
            ? getRestrictionMessage('수정', currentUser)
            : !isExpiredToday
              ? '환불된 슬롯은 종료일이 오늘인 경우에만 연장 가능합니다.'
              : undefined
      };

    case '종료':
      return {
        canExtend: false,
        canEdit: false,
        canBatchEdit: false,
        canRefund: false,
        // 종료된 슬롯은 ADMIN만 삭제 가능
        canDelete: isAdmin,
        reason: '종료된 슬롯은 삭제만 가능합니다.'
      };

    default:
      return {
        canExtend: false,
        canEdit: false,
        canBatchEdit: false,
        canRefund: false,
        canDelete: false,
        reason: '알 수 없는 상태입니다.'
      };
  }
}

/**
 * 선택된 슬롯들에 대한 일괄 권한을 확인합니다.
 */
export function getBatchPermissions(
  slots: Slot[],
  currentUser: User | null,
  isRestricted: boolean = false
): {
  canExtend: boolean;
  canEdit: boolean;
  canBatchEdit: boolean;
  canRefund: boolean;
  canDelete: boolean;
  hasRefunded: boolean;
  hasNotStarted: boolean;
  reason?: string;
} {
  if (slots.length === 0) {
    return {
      canExtend: false,
      canEdit: false,
      canBatchEdit: false,
      canRefund: false,
      canDelete: false,
      hasRefunded: false,
      hasNotStarted: false,
      reason: '선택된 슬롯이 없습니다.'
    };
  }

  const isAdmin = currentUser?.grade === 'ADMIN';
  const timeRestricted = currentUser ? isSlotOperationRestricted(currentUser) : false;
  
  // 미개시 슬롯이 있는지 확인 (시작일이 오늘 이후인 슬롯)
  const today = getTodayDateOnly();

  let canExtend = true;
  let canEdit = true;
  let canBatchEdit = true;
  let canRefund = true;
  let canDelete = true;
  let hasRefunded = false;
  let hasNotStarted = false;
  let hasActive = false;

  for (const slot of slots) {
    const p = getSlotPermissions(slot, currentUser, isRestricted);
    if (!p.canExtend) canExtend = false;
    if (!p.canEdit) canEdit = false;
    if (!p.canBatchEdit) canBatchEdit = false;
    if (!p.canRefund) canRefund = false;
    if (!p.canDelete) canDelete = false;

    if (slot.status === '환불') hasRefunded = true;

    if (slot.start_date) {
      const startDate = toLocalDateOnly(slot.start_date);
      if (startDate && startDate > today) {
        hasNotStarted = true;
      }

      if (slot.end_date) {
        const endDate = toLocalDateOnly(slot.end_date);
        if (startDate && endDate && startDate <= today && endDate > today) {
          hasActive = true;
        }
      }
    }
  }

  // 미개시 슬롯 포함 시 환불 불가 처리 (사유 메시지는 환불 문맥에서만 표시해야 하므로 여기서는 설정하지 않음)
  if (hasNotStarted) {
    canRefund = false;
  }

  let reason: string | undefined;
  if (timeRestricted || isRestricted) {
    reason = getRestrictionMessage('수정', currentUser);
  } else if (hasRefunded && !canRefund) {
    reason = '환불된 슬롯이 포함되어 있어 환불이 불가능합니다.';
  } else if (hasActive && !canDelete) {
    reason = '작동 중인 슬롯이 포함되어 있어 삭제가 불가능합니다.';
  }

  return {
    canExtend,
    canEdit,
    canBatchEdit,
    canRefund,
    canDelete,
    hasRefunded,
    hasNotStarted,
    reason
  };
}

/**
 * 슬롯이 삭제 가능한지 확인합니다.
 */
export function canDeleteSlot(slot: Slot, currentUser: User | null): boolean {
  if (!currentUser) {
    return false;
  }

  // 종료된 슬롯: ADMIN만 삭제 가능
  if (slot.status === '종료') {
    return currentUser.grade === 'ADMIN';
  }

  // 정책: 미개시(내일 이후) 슬롯만 삭제 가능 (정상/연장)
  if (slot.status !== '정상' && slot.status !== '연장') return false;
  if (!slot.start_date) return false;

  const today = getTodayDateOnly();
  const startDate = toLocalDateOnly(slot.start_date);
  // adpa99는 이미 시작된 슬롯도 삭제 가능 (셀러/시간제한 제외)
  if (startDate && startDate <= today) {
    if (!isAdpa99Username(currentUser.username)) return false;
    if (currentUser.grade === 'SELLER') return false;
    if (isSlotOperationRestricted(currentUser)) return false;
    return true;
  }
  if (!startDate || !(startDate > today)) return false;

  // ADMIN: (미개시 정상) 삭제 가능 여부는 기존 정책 따름 (필요시 무시 가능)
  if (currentUser.grade === 'ADMIN') {
    return true;
  }

  // SELLER: 삭제 불가
  if (currentUser.grade === 'SELLER') {
    return false;
  }

  // 대행사/총판: 제한 시간 외에만 가능
  const timeRestricted = isSlotOperationRestricted(currentUser);
  if (timeRestricted) return false;

  return true;
}

/**
 * 슬롯이 연장 가능한지 확인합니다.
 */
export function canExtendSlot(slot: Slot, currentUser: User | null): boolean {
  if (!currentUser) return false;
  if (currentUser.grade === 'SELLER') return false;

  const timeRestricted = isSlotOperationRestricted(currentUser);
  if (timeRestricted) return false;

  switch (slot.status) {
    case '정상':
    case '연장':
      return true;
    case '환불':
      return isSameDateOnly(toLocalDateOnly(slot.end_date), getTodayDateOnly());
    case '종료':
      return false;
    default:
      return false;
  }
} 

/**
 * 슬롯이 시작 전인지 확인
 * @param slot 슬롯 객체
 * @returns 시작 전이면 true, 시작했으면 false
 */
export function isSlotBeforeStart(slot: Slot): boolean {
  if (!slot.start_date) return false;
  
  const today = getTodayDateOnly();
  const startDate = toLocalDateOnly(slot.start_date);
  if (!startDate) return false;
  
  return today < startDate;
}

/**
 * 슬롯이 환불 가능한지 확인
 * @param slot 슬롯 객체
 * @returns 환불 가능하면 true, 불가능하면 false
 */
export function canRefundSlot(slot: Slot): boolean {
  // 시작 전 슬롯은 환불 불가
  if (isSlotBeforeStart(slot)) {
    return false;
  }
  
  // 이미 환불된 슬롯은 환불 불가
  if (slot.status === '환불') {
    return false;
  }
  
  return true;
} 